import {jwtDecode} from 'jwt-decode';

const STORAGE_KEY_ACCESS = 'local_access_token';
// Pre-cookie clients kept the refresh token here. New sessions never write this
// key; it is only read so an in-flight legacy session can migrate to the cookie.
const STORAGE_KEY_LEGACY_REFRESH = 'local_refresh_token';
// Non-sensitive marker that a refresh cookie probably exists, so a fresh
// window/panel knows to attempt silent restore before showing the login page.
// localStorage on purpose: it must survive the browsing context that set it.
const STORAGE_KEY_SESSION_HINT = 'ta_session_hint';

// The short-lived access token stays in sessionStorage (consistent with the MSAL
// cache). The 30-day refresh token lives only in an HttpOnly cookie set by the
// server and is never readable from JavaScript.
export function getLocalAccessToken(): string | null {
    return sessionStorage.getItem(STORAGE_KEY_ACCESS) || null;
}

export function storeLocalAccessToken(accessToken: string): void {
    sessionStorage.setItem(STORAGE_KEY_ACCESS, accessToken);
}

export function clearLocalTokens(): void {
    sessionStorage.removeItem(STORAGE_KEY_ACCESS);
    sessionStorage.removeItem(STORAGE_KEY_LEGACY_REFRESH);
}

export function getLegacyRefreshToken(): string | null {
    return sessionStorage.getItem(STORAGE_KEY_LEGACY_REFRESH) || null;
}

export function clearLegacyRefreshToken(): void {
    sessionStorage.removeItem(STORAGE_KEY_LEGACY_REFRESH);
}

export function hasSessionHint(): boolean {
    return localStorage.getItem(STORAGE_KEY_SESSION_HINT) === '1';
}

export function setSessionHint(): void {
    localStorage.setItem(STORAGE_KEY_SESSION_HINT, '1');
}

export function clearSessionHint(): void {
    localStorage.removeItem(STORAGE_KEY_SESSION_HINT);
}

const TEN_SECONDS_MS = 10_000;

export function localTokenHasExpired(accessToken: string): boolean {
    try {
        const decoded = jwtDecode<{exp: number}>(accessToken);
        const expiryMs = decoded.exp * 1000;
        return Date.now() >= expiryMs - TEN_SECONDS_MS;
    } catch {
        return true; // treat decode error as expired
    }
}

const TWO_MINUTES_MS = 120_000;

/**
 * Returns the absolute token expiry time (ms since epoch), or null if it cannot
 * be decoded. Used to schedule a proactive refresh timer precisely.
 */
export function getLocalTokenExpiryMs(accessToken: string): number | null {
    try {
        const decoded = jwtDecode<{exp: number}>(accessToken);
        return decoded.exp * 1000;
    } catch {
        return null;
    }
}

/**
 * True when the token will expire within `withinMs` (default 2 min). Used by the
 * wake-on-focus handler to refresh proactively before a burst of requests fires.
 * A token that cannot be decoded is treated as expiring soon.
 */
export function localTokenExpiringSoon(
    accessToken: string,
    withinMs: number = TWO_MINUTES_MS
): boolean {
    const expiryMs = getLocalTokenExpiryMs(accessToken);
    if (expiryMs === null) {
        return true;
    }

    return Date.now() >= expiryMs - withinMs;
}

export function hasValidLocalToken(): boolean {
    const token = getLocalAccessToken();
    if (!token) {
        return false;
    }

    return !localTokenHasExpired(token);
}

export type LocalTokenClaims = {
    oid?: string;
    email?: string;
    given_name?: string;
    family_name?: string;
    upn?: string;
};

export function decodeLocalToken(accessToken: string): LocalTokenClaims {
    try {
        return jwtDecode<LocalTokenClaims>(accessToken);
    } catch {
        return {};
    }
}
